import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import { Text } from './Text'
import { Button } from '@/components/ui/button'
import { ChevronDownIcon } from './Icons'

export interface SectionAction {
  content: string
  onAction: () => void
}

export interface SectionProps {
  /** Section title */
  title?: string
  /** Section description */
  description?: React.ReactNode
  /** Optional action button */
  action?: SectionAction
  /** Custom content rendered in the header row, between title/description and action button */
  headerActions?: React.ReactNode
  /** Section content */
  children?: React.ReactNode
  /** Subdued background */
  subdued?: boolean
  /** Make section collapsible */
  collapsible?: boolean
  /** Default collapsed state */
  defaultCollapsed?: boolean
  /** Additional CSS classes */
  className?: string
  /** Data ID for testing */
  'data-id'?: string
}

export const Section: React.FC<SectionProps> = ({
  title,
  description,
  action,
  headerActions,
  children,
  subdued = false,
  collapsible = false,
  defaultCollapsed = false,
  className,
  'data-id': dataId,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

  const hasHeader = title || description || action || headerActions || collapsible

  return (
    <section
      data-id={dataId}
      className={cn(
        'rounded-lg border border-border shadow-[0_1px_0_rgba(0,0,0,0.05)]',
        subdued ? 'bg-muted' : 'bg-background',
        className
      )}
    >
      {hasHeader && (
        <div className="flex items-center justify-between gap-4 flex-wrap px-5 pt-5 pb-4">
          <div className="flex-1 min-w-0">
            {title && (
              <div className="flex items-center gap-2">
                <Text
                  as="h2"
                  size="body"
                  className="font-[550]"
                  spacing="none"
                >
                  {title}
                </Text>
                {collapsible && (
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="inline-flex items-center justify-center w-6 h-6 text-muted-foreground hover:text-accent-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-label={isCollapsed ? 'Expandir seção' : 'Recolher seção'}
                  >
                    <ChevronDownIcon
                      className={cn(
                        'w-4 h-4 transition-transform duration-200',
                        !isCollapsed && 'rotate-180'
                      )}
                    />
                  </button>
                )}
              </div>
            )}
            {description && (
              <Text as="div" size="small" color="subdued" spacing="none">
                {description}
              </Text>
            )}
          </div>
          {headerActions}
          {action && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={action.onAction}
            >
              {action.content}
            </Button>
          )}
        </div>
      )}
      {collapsible ? (
        <div
          className={cn(
            'overflow-hidden transition-all duration-200 ease-in-out',
            isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
          )}
        >
          {children && (
            <div className={hasHeader ? 'px-5 pb-5' : 'p-5'}>{children}</div>
          )}
        </div>
      ) : (
        children && (
          <div className={hasHeader ? 'px-5 pb-5' : 'p-5'}>{children}</div>
        )
      )}
    </section>
  )
}
